import {
  Button,
  Link,
  Text,
  VisuallyHidden,
} from '@spotify-internal/encore-web';
import { LoadingOverlay } from '@spotify/confidence-core-react';

import { TransitionScreen } from '../TransitionScreen';
import {
  CreateAccountFormProvider,
  type SignupInvite,
  useCreateAccountFormContext,
} from './CreateAccountFormProvider';
import { ExperimentationLoader } from './ExperimentationLoader';
import { WelcomeScreen } from './WelcomeScreen';
import { WorkspaceSetupForm } from './WorkspaceSetupForm';
import {
  type CreatedAccount,
  SignupSessionExpiredError,
  expiredSessionMessage,
} from './hooks';

import styles from './CreateAccountForm.module.css';

const contactSupportLink = (
  <Link
    href="mailto:confidence-support@spotify.com"
    target="_blank"
    rel="noopener noreferrer"
   data-track-source-anchor="442c6e3491">
    Contact support <VisuallyHidden>(opens in new window)</VisuallyHidden>
  </Link>
);

const verificationEmailHelperText = (
  <>
    Didn&apos;t receive the verification email? Check your spam folder or{' '}
    {contactSupportLink}.
  </>
);

const InviteEmailMismatchDescription = ({
  inviteEmail,
  currentUserEmail,
}: {
  inviteEmail?: string;
  currentUserEmail?: string;
}) => {
  if (!inviteEmail) {
    return (
      <>
        We couldn&apos;t confirm the email address for this invite. Ask the
        sender for a new invite.
      </>
    );
  }
  if (currentUserEmail) {
    return (
      <>
        This invite was sent to a different email address, but you&apos;re
        signed in as <span data-csr-mask>{currentUserEmail}</span>. Switch
        accounts to continue.
      </>
    );
  }
  return (
    <>
      We couldn&apos;t confirm the email address on your account. Sign in with
      the email address that received this invite to continue.
    </>
  );
};

const CreateAccountFormContent = () => {
  const {
    screen,
    showOverlay,
    error,
    handleResultDismissed,
    recheckEmailVerification,
    handleLogin,
    handleLogout,
    handleSelfServeSignup,
    currentUserEmail,
    inviteEmail,
    emailVerificationStatus,
  } = useCreateAccountFormContext();

  const verificationStatusText = {
    idle: undefined,
    checking: 'Checking your email verification status…',
    pending:
      "We haven't detected verification yet. Wait a moment, then try again.",
    error: "We couldn't check your verification status. Try again.",
    sign_in_required: expiredSessionMessage,
  }[emailVerificationStatus];

  const isSignInRequired = emailVerificationStatus === 'sign_in_required';
  let verificationActionLabel = "I've verified my email";

  if (emailVerificationStatus === 'checking') {
    verificationActionLabel = 'Checking…';
  } else if (isSignInRequired) {
    verificationActionLabel = 'Log in';
  }

  switch (screen) {
    case 'loading':
      return <LoadingOverlay loading />;

    case 'verify_email':
      return (
        <TransitionScreen
          title="Verify your email address"
          description={
            <>
              We sent a verification link to{' '}
              {currentUserEmail ? (
                <span data-csr-mask>{currentUserEmail}</span>
              ) : (
                'your email'
              )}
              . Open it to continue.
              {verificationStatusText && (
                <Text
                  as="div"
                  variant="bodySmall"
                  semanticColor={
                    emailVerificationStatus === 'error'
                      ? 'textNegative'
                      : 'textSubdued'
                  }
                  role="status"
                >
                  {verificationStatusText}
                </Text>
              )}
            </>
          }
          actions={
            <>
              <Button
                variant="primary"
                size="medium"
                disabled={emailVerificationStatus === 'checking'}
                onClick={
                  isSignInRequired ? handleLogout : recheckEmailVerification
                }
               data-track-source-anchor="4e025e33fa;create-account-form-content-button">
                {verificationActionLabel}
              </Button>
              {!isSignInRequired && (
                <Button
                  variant="secondary"
                  size="medium"
                  onClick={handleLogout}
                 data-track-source-anchor="9f14c6d041">
                  Use a different account
                </Button>
              )}
            </>
          }
          helperText={verificationEmailHelperText}
        />
      );

    case 'under_review':
      return (
        <TransitionScreen
          title="Thank you for signing up for Confidence"
          description="We're reviewing your account request. We'll email you once it's approved."
          helperText={<>Have questions? {contactSupportLink}.</>}
        />
      );

    case 'workspace_setup':
      return (
        <>
          {showOverlay && (
            <div className={styles.overlay}>
              <ExperimentationLoader
                error={error}
                onResultDismissed={handleResultDismissed}
                onSignInAgain={
                  error instanceof SignupSessionExpiredError
                    ? handleLogout
                    : undefined
                }
              />
            </div>
          )}
          <WorkspaceSetupForm />
        </>
      );

    case 'invite_email_mismatch':
      return (
        <TransitionScreen
          title="Use the email address from your invite"
          description={
            <InviteEmailMismatchDescription
              inviteEmail={inviteEmail}
              currentUserEmail={currentUserEmail}
            />
          }
          actions={
            inviteEmail ? (
              <Button
                variant="primary"
                size="medium"
                onClick={() => handleLogin()}
               data-track-source-anchor="0a0532b360">
                Use a different account
              </Button>
            ) : (
              <Button
                variant="primary"
                size="medium"
                onClick={handleSelfServeSignup}
               data-track-source-anchor="0bc17e5c0d">
                Go to signup
              </Button>
            )
          }
          helperText={<>Need help? {contactSupportLink}.</>}
        />
      );

    case 'login_verify_email':
      return (
        <div data-track-id="region.verify-email" data-track-impression>
          <TransitionScreen
            title="Verify your email address"
            description="Open the verification link we sent to your email, then continue."
            actions={
              <Button
                variant="primary"
                size="medium"
                onClick={() => handleLogin()}
               data-track-source-anchor="27871cac40">
                Continue after verifying
              </Button>
            }
            helperText={verificationEmailHelperText}
          />
        </div>
      );

    case 'welcome':
      return (
        <div data-track-id="region.welcome" data-track-impression>
          <WelcomeScreen onContinue={() => handleLogin()} />
        </div>
      );

    default: {
      const _exhaustive: never = screen;
      return _exhaustive;
    }
  }
};

export const CreateAccountForm = ({
  invite,
  inviteId,
  hasAuthCallback,
  onCreate,
}: {
  inviteId?: string;
  invite?: SignupInvite;
  hasAuthCallback?: boolean;
  onCreate: (account?: CreatedAccount) => void;
}) => (
  <CreateAccountFormProvider
    invite={invite}
    inviteId={inviteId}
    hasAuthCallback={hasAuthCallback}
    onCreate={onCreate}
  >
    <CreateAccountFormContent />
  </CreateAccountFormProvider>
);
